import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Users, Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface MemorySharingProps {
  // Content Props
  title?: string;
  subtitle?: string;
  
  // Display Props
  showTeamMemory?: boolean;
  showIsolatedMemory?: boolean;
  
  // Style Props
  className?: string;
  variant?: 'default' | 'compact';
  
  // Animation Props
  enableAnimations?: boolean;
  
  // Team Memory Props
  teamAgents?: string[];
  teamDescription?: string;
  
  // Isolated Memory Props
  isolatedAgents?: string[];
  isolatedDescription?: string;
}

const MemorySharing: React.FC<MemorySharingProps> = ({
  title,
  subtitle,
  showTeamMemory = true,
  showIsolatedMemory = true,
  className = "",
  variant = 'default',
  enableAnimations = true,
  teamAgents,
  teamDescription,
  isolatedAgents,
  isolatedDescription,
  ...props
}) => {
  const { t } = useTranslation();

  // Default content with i18n
  const defaultTitle = title || t('memorySharing.title', { ns: 'homepage' });
  const defaultSubtitle = subtitle || t('memorySharing.description', { ns: 'homepage' });
  
  const defaultTeamAgents = teamAgents || [
    t('memorySharing.teamMemory.agentA', { ns: 'homepage' }),
    t('memorySharing.teamMemory.agentB', { ns: 'homepage' }),
    t('memorySharing.teamMemory.agentC', { ns: 'homepage' })
  ];
  
  const defaultIsolatedAgents = isolatedAgents || [
    t('memorySharing.isolatedMemory.agentA', { ns: 'homepage' }),
    t('memorySharing.isolatedMemory.agentB', { ns: 'homepage' }),
    t('memorySharing.isolatedMemory.agentC', { ns: 'homepage' })
  ];
  
  const defaultTeamDescription = teamDescription || t('memorySharing.teamMemory.description', { ns: 'homepage' });
  const defaultIsolatedDescription = isolatedDescription || t('memorySharing.isolatedMemory.description', { ns: 'homepage' });

  // Animation variants
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  const cardVariants = {
    team: {
      hidden: { opacity: 0, x: -20 },
      visible: { 
        opacity: 1, 
        x: 0,
        transition: { duration: 0.6 }
      },
      hover: { y: -4 }
    },
    isolated: {
      hidden: { opacity: 0, x: 20 },
      visible: { 
        opacity: 1, 
        x: 0,
        transition: { duration: 0.6, delay: 0.2 }
      },
      hover: { y: -4 }
    }
  };

  // Variant classes
  const variantClasses = {
    default: "space-y-8 py-16 bg-muted/30",
    compact: "space-y-6 py-12 bg-muted/20"
  };

  // Motion components
  const MotionSection = enableAnimations ? motion.section : 'section';
  const MotionDiv = enableAnimations ? motion.div : 'div';

  return (
    <MotionSection 
      variants={enableAnimations ? itemVariants : undefined}
      initial={enableAnimations ? "hidden" : undefined}
      animate={enableAnimations ? "visible" : undefined}
      className={`${variantClasses[variant]} ${className}`}
      {...props}
    >
      {/* Section Header */}
      <div className="text-center space-y-4">
        <h2 className="text-3xl font-bold text-foreground">{defaultTitle}</h2>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
          {defaultSubtitle}
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
        {/* Team Memory Space */}
        {showTeamMemory && (
          <MotionDiv
            variants={enableAnimations ? cardVariants.team : undefined}
            initial={enableAnimations ? "hidden" : undefined}
            animate={enableAnimations ? "visible" : undefined}
            whileHover={enableAnimations ? "hover" : undefined}
          >
            <Card className="h-full border-green-200 bg-green-50/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-green-600" />
                  {t('memorySharing.teamMemory.title', { ns: 'homepage' })}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-4">
                    {defaultTeamAgents.map((agent) => (
                      <div key={agent} className="text-center p-3 bg-white rounded-lg border border-green-200">
                        <div className="font-medium text-sm">{agent}</div>
                        <div className="text-xs text-muted-foreground">
                          {t('memorySharing.teamMemory.sharesContext', { ns: 'homepage' })}
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-muted-foreground text-center">
                    {defaultTeamDescription}
                  </p>
                </div>
              </CardContent>
            </Card>
          </MotionDiv>
        )}

        {/* Isolated Memory Spaces */}
        {showIsolatedMemory && (
          <MotionDiv
            variants={enableAnimations ? cardVariants.isolated : undefined}
            initial={enableAnimations ? "hidden" : undefined}
            animate={enableAnimations ? "visible" : undefined}
            whileHover={enableAnimations ? "hover" : undefined}
          >
            <Card className="h-full border-blue-200 bg-blue-50/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Lock className="w-5 h-5 text-blue-600" />
                  {t('memorySharing.isolatedMemory.title', { ns: 'homepage' })}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-4">
                    {defaultIsolatedAgents.map((agent) => (
                      <div key={agent} className="text-center p-3 bg-white rounded-lg border border-blue-200">
                        <div className="font-medium text-sm">{agent}</div>
                        <div className="text-xs text-muted-foreground">
                          {t('memorySharing.isolatedMemory.privateMemory', { ns: 'homepage' })}
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-muted-foreground text-center">
                    {defaultIsolatedDescription}
                  </p>
                </div>
              </CardContent>
            </Card>
          </MotionDiv>
        )}
      </div>
    </MotionSection>
  );
};

export default MemorySharing;
export type { MemorySharingProps };
